import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs } from '@/components/ui';
import { canView } from '@/shared/lib/permissions';
import { useAuthStore } from '@/shared/stores/auth';
import { adminApi } from '@/shared/api/adminApi';
import { Leads } from './Leads';
import { Crm } from './Crm';

/**
 * "לידים ואנשי קשר" — פריט תפריט אחד (07/09/2026): מאגר הלידים של הקמפיינים
 * ואנשי הקשר (CRM) בלשוניות. /admin/crm מפנה לכאן עם ?tab=contacts.
 * עיצוב v2: בורר הלשוניות (עם ספירות) יושב בשורה אחת עם פעולות המסך,
 * ולכן הוא מועבר ללשונית הפעילה שמציירת את השורה.
 */
const ALL_TABS = [
  { id: 'leads', label: 'לידים', perm: 'leads' },
  { id: 'contacts', label: 'אנשי קשר', perm: 'crm' },
];

export function LeadsContacts() {
  const [params, setParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const tabs = ALL_TABS.filter((t) => canView(user, t.perm));
  const requested = params.get('tab') ?? 'leads';
  const tab = tabs.some((t) => t.id === requested) ? requested : (tabs[0]?.id ?? 'leads');
  const [counts, setCounts] = useState<Record<string, number>>({});
  const setCount = (id: string) => (n: number) => setCounts((c) => (c[id] === n ? c : { ...c, [id]: n }));

  // ספירה ראשונית גם ללשונית שאינה פעילה; הלשונית הפעילה מעדכנת בעצמה.
  const canLeads = tabs.some((t) => t.id === 'leads');
  const canContacts = tabs.some((t) => t.id === 'contacts');
  useEffect(() => {
    if (canLeads) void adminApi.leadStats(null).then((s) => setCount('leads')((s as { total: number }).total)).catch(() => {});
    if (canContacts) void adminApi.contactStats().then((s) => setCount('contacts')((s as { total: number }).total)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLeads, canContacts]);

  const switcher = tabs.length > 1 ? (
    <Tabs
      items={tabs.map((t) => ({ id: t.id, label: t.label, count: counts[t.id] }))}
      value={tab}
      onChange={(id) => setParams(id === 'leads' ? {} : { tab: id }, { replace: true })}
    />
  ) : undefined;

  return (
    <>
      {tab === 'leads' && <Leads switcher={switcher} onTotal={setCount('leads')} />}
      {tab === 'contacts' && <Crm switcher={switcher} onTotal={setCount('contacts')} />}
    </>
  );
}
